<template>
  <div class="express-checkout-container">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>Initializing {{ getPaymentMethodName(currentMethod) }}...</p>
    </div>

    <!-- Processing State -->
    <div v-if="isProcessing" class="processing-container">
      <div class="processing-spinner"></div>
      <p>Processing your {{ getPaymentMethodName(currentMethod) }} payment...</p>
      <p class="processing-note">Please don't close this window</p>
    </div>

    <!-- Success State -->
    <div v-if="isCompleted" class="success-container">
      <div class="success-icon">✓</div>
      <h3>Payment Successful!</h3>
      <p>Your payment via {{ getPaymentMethodName(currentMethod) }} has been processed successfully.</p>
      <p><strong>Order ID:</strong> {{ orderId }}</p>
      <p><strong>Transaction ID:</strong> {{ transactionId }}</p>
      <p><strong>Amount:</strong> {{ formatAmount(totalWithShippingAndTax, currency) }}</p>
      <button @click="redirectToSuccess" class="success-btn">Continue</button>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-container">
      <div class="error-icon">✗</div>
      <h3>Payment Error</h3>
      <p>{{ error }}</p>
      <button @click="resetCheckout" class="retry-btn">Try Again</button>
    </div>

    <!-- Hidden PayPal Container (will be shown in popup) -->
    <div id="express-paypal-button-container" style="display: none;"></div>
    
    <!-- PayPal Popup -->
    <div v-if="showPayPalPopup" class="payment-popup-overlay" @click="closePaymentPopup">
      <div class="payment-popup paypal-popup" @click.stop>
        <div class="popup-header">
          <h3>PayPal Express Checkout</h3>
          <button @click="closePaymentPopup" class="close-btn">&times;</button>
        </div>
        <div class="popup-content">
          <div class="payment-summary">
            <h4>Payment Summary</h4>
            <div class="amount-breakdown">
              <span>Subtotal:</span>
              <span>{{ formatAmount(amount, currency) }}</span>
            </div>
            <div class="amount-breakdown" v-if="dynamicShippingAmount > 0">
              <span>Shipping:</span>
              <span>{{ formatAmount(dynamicShippingAmount, currency) }}</span>
            </div>
            <div class="amount-breakdown" v-if="dynamicTaxAmount > 0">
              <span>Tax:</span>
              <span>{{ formatAmount(dynamicTaxAmount, currency) }}</span>
            </div>
            <div class="amount-breakdown" v-if="dynamicShippingTaxAmount > 0">
              <span>Shipping Tax:</span>
              <span>{{ formatAmount(dynamicShippingTaxAmount, currency) }}</span>
            </div>
            <div class="amount-display">{{ formatAmount(totalWithShippingAndTax, currency) }}</div>
            <div class="merchant-info">{{ merchantName }}</div>
            <div class="items-list">
              <div v-for="item in items" :key="item.id" class="item-row">
                <span class="item-name">{{ item.name }} (x{{ item.quantity }})</span>
                <span class="item-price">{{ formatAmount(item.price * item.quantity, currency) }}</span>
              </div>
            </div>
          </div>
          <div id="paypal-popup-container"></div>
        </div>
      </div>
    </div>

    <!-- Address Confirmation Popup -->
    <div v-if="showAddressConfirmation" class="payment-popup-overlay" @click="handleOverlayClick">
      <div class="payment-popup address-popup" @click.stop>
        <div class="popup-header">
          <h3>Confirm Shipping Address</h3>
          <button @click="closeAddressConfirmation" class="close-btn">&times;</button>
        </div>
        <div class="popup-content">
          <div v-if="!editingAddress" class="address-confirmation">
            <p class="confirmation-text">Please confirm your shipping address:</p>
            <div class="address-display">
              <div class="address-field">
                <strong>Name:</strong> {{ collectedShippingAddress?.name || 'Not provided' }}
              </div>
              <div class="address-field">
                <strong>Email:</strong> {{ collectedShippingAddress?.email || 'Not provided' }}
              </div>
              <div class="address-field">
                <strong>Phone:</strong> {{ collectedShippingAddress?.phone || 'Not provided' }}
              </div>
              <div class="address-field">
                <strong>Address:</strong><br>
                {{ collectedShippingAddress?.address || 'Not provided' }}<br>
                <span v-if="collectedShippingAddress?.address2">{{ collectedShippingAddress.address2 }}<br></span>
                {{ collectedShippingAddress?.city || '' }} {{ collectedShippingAddress?.state || '' }} {{ collectedShippingAddress?.postal_code || '' }}<br>
                {{ collectedShippingAddress?.country || '' }}
              </div>
            </div>
            <div class="address-actions">
              <button @click.prevent.stop="handleConfirmAndProceedClick" type="button" class="confirm-btn" :disabled="!collectedShippingAddress">Use This Address</button>
              <button @click="editAddress" class="edit-btn">Edit Address</button>
            </div>
          </div>

          <!-- Address Editing Form -->
          <div v-if="editingAddress" class="address-form">
            <h4>Edit Shipping Address</h4>
            <div class="form-grid">
              <div class="form-field">
                <label>First Name *</label>
                <input v-model="editableAddress.first_name" type="text" required>
              </div>
              <div class="form-field">
                <label>Last Name *</label>
                <input v-model="editableAddress.last_name" type="text" required>
              </div>
              <div class="form-field full-width">
                <label>Email *</label>
                <input v-model="editableAddress.email" type="email" required>
              </div>
              <div class="form-field full-width">
                <label>Phone *</label>
                <input v-model="editableAddress.phone" type="tel" required>
              </div>
              <div class="form-field full-width">
                <label>Address Line 1 *</label>
                <input v-model="editableAddress.address" type="text" required>
              </div>
              <div class="form-field full-width">
                <label>Address Line 2</label>
                <input v-model="editableAddress.address2" type="text">
              </div>
              <div class="form-field">
                <label>City *</label>
                <input v-model="editableAddress.city" type="text" required>
              </div>
              <div class="form-field">
                <label>State *</label>
                <input v-model="editableAddress.state" type="text" required>
              </div>
              <div class="form-field">
                <label>Postal Code *</label>
                <input v-model="editableAddress.postal_code" type="text" required>
              </div>
              <div class="form-field">
                <label>Country *</label>
                <select v-model="editableAddress.country" required>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                </select>
              </div>
            </div>
            <div class="form-actions">
              <button @click.prevent.stop="handleSaveEditedAddressClick" type="button" class="save-btn" :disabled="!isAddressValid">Save & Continue</button>
              <button @click="cancelEdit" class="cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Google Pay Container -->
    <div id="google-pay-button-container" style="display: none;"></div>
    <!-- Apple Pay Container -->
    <div id="apple-pay-button-container" style="display: none;"></div>
  </div>
</template>

<script>
import axios from 'axios'
import { useCartStore } from "../../stores/cart";
import { useLoginStore } from "../../stores/login";
import { useRouter } from 'vue-router';


export default {
  name: 'UnifiedExpressCheckout',
  setup() {
    const cartData = useCartStore();
    const router = useRouter();
    const userLogin = useLoginStore();
    return {
      cartData,
      router,
      userLogin
    }
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    amount: {
      type: Number,
      required: true
    },
    currency: {
      type: String,
      default: 'USD'
    },
    userId: {
      type: Number,
      required: true
    },
    shippingAddress: {
      type: Object,
      default: null
    },
    merchantName: {
      type: String,
      default: 'Your Store'
    },
    stripePublishableKey: {
      type: String,
      required: true
    },
    baseURL: {
      type: String,
      default: ''
    },
    shippingAmount: {
      type: Number,
      default: 0
    },
    taxAmount: {
      type: Number,
      default: 0
    },
  },
  data() {
    return {
      isLoading: false,
      isProcessing: false,
      isCompleted: false,
      error: null,
      currentMethod: null,
      checkoutId: null,
      orderId: null,
      transactionId: null,
      
      // Dynamic amounts
      dynamicShippingAmount: 0,
      dynamicTaxAmount: 0,
      dynamicShippingTaxAmount: 0,
      dynamicTotalAmount: 0,
      
      // PayPal specific
      showPayPalPopup: false,
      paypalInstance: null,
      loadedSDKCurrency: null,  // Track which currency the SDK was loaded with
      
      // Address confirmation
      showAddressConfirmation: false,
      collectedShippingAddress: null,
      editingAddress: false,
      
      // Google Pay specific
      googlePayClient: null,
      isGooglePayAvailable: false,
      
      // Apple Pay specific
      isApplePayAvailable: false,
      
      // Pending payment data for address confirmation flow
      pendingPaymentData: null,
      
      // Button references
      paypalButton: null,
      gpayButton: null,
      applePayButton: null,
      
      // Address editing
      editableAddress: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US'
      }
    }
  },
  computed: {
    canCheckout() {
      const hasItems = this.items && this.items.length > 0;
      const hasAmount = this.amount && this.amount > 0;
      const hasUserId = this.userId && this.userId > 0;
      
      console.log('🔍 canCheckout debug:', {
        items: this.items,
        hasItems,
        amount: this.amount,
        hasAmount,
        userId: this.userId,
        hasUserId,
        result: hasItems && hasAmount && hasUserId
      });
      
      return hasItems && hasAmount && hasUserId;
    },
    totalWithShippingAndTax() {
      return this.amount + this.dynamicShippingAmount + this.dynamicTaxAmount + this.dynamicShippingTaxAmount;
    },
    isAddressValid() {
      return this.editableAddress.first_name && 
             this.editableAddress.last_name && 
             this.editableAddress.email && 
             this.editableAddress.phone && 
             this.editableAddress.address && 
             this.editableAddress.city && 
             this.editableAddress.state && 
             this.editableAddress.postal_code && 
             this.editableAddress.country;
    }
  },
  watch: {
    // Watch for currency prop changes
    currency: {
      immediate: false,
      async handler(newValue, oldValue) {
        console.log('💰 [CURRENCY_WATCH] Currency prop changed:', { old: oldValue, new: newValue });

        // If currency changes and PayPal SDK is already loaded, we need to reload it
        if (oldValue && newValue && oldValue !== newValue && window.paypal) {
          console.warn('⚠️ [CURRENCY_WATCH] Currency changed after PayPal SDK loaded!');
          console.warn('⚠️ [CURRENCY_WATCH] PayPal SDK needs to be reloaded for new currency');
          console.warn('⚠️ [CURRENCY_WATCH] Please refresh the page or use a single currency per session');

          // Remove old PayPal SDK script
          const oldScripts = document.querySelectorAll('script[src*="paypal.com/sdk/js"]');
          oldScripts.forEach(script => script.remove());

          // Clear PayPal from window
          delete window.paypal;

          // Reset loaded currency tracker
          this.loadedSDKCurrency = null;

          // Reload PayPal SDK with new currency
          console.log('🔄 [CURRENCY_WATCH] Reloading PayPal SDK with new currency:', newValue);
          await this.loadPayPalSDK();

          console.log('✅ [CURRENCY_WATCH] PayPal SDK reloaded successfully with currency:', newValue);
        }
      }
    },
    // Watch for address confirmation modal visibility
    showAddressConfirmation(newValue) {
      if (newValue) {
        console.log('👁️ [MODAL_WATCH] Address confirmation modal is now visible')
        console.log('👁️ [MODAL_WATCH] Current state:', {
          collectedShippingAddress: this.collectedShippingAddress,
          pendingPaymentData: !!this.pendingPaymentData,
          editingAddress: this.editingAddress,
          checkoutId: this.checkoutId
        })
      } else {
        console.log('👁️ [MODAL_WATCH] Address confirmation modal is now hidden')
      }
    }
  },
  async mounted() {
    console.log('🚀 [MOUNTED] UnifiedExpressCheckout props:', {
      currency: this.currency,
      amount: this.amount,
      items: this.items,
      userId: this.userId
    });
    
    await this.loadPayPalSDK()
    await this.loadGooglePaySDK()
    await this.initializePaymentMethods()
    this.attachEventListeners()
    
    // Add page unload detector to catch unexpected redirects
    window.addEventListener('beforeunload', (event) => {
      console.log('🚨 [PAGE_UNLOAD] Page is about to unload/redirect!')
      console.log('🚨 [PAGE_UNLOAD] Current URL:', window.location.href)
      console.log('🚨 [PAGE_UNLOAD] Component state:', {
        showAddressConfirmation: this.showAddressConfirmation,
        isLoading: this.isLoading,
        isProcessing: this.isProcessing,
        pendingPaymentData: !!this.pendingPaymentData,
        collectedShippingAddress: !!this.collectedShippingAddress
      })
    })
  },
  beforeUnmount() {
    this.removeEventListeners()
  },
  methods: {
    async loadPayPalSDK() {
      // Use the currency from props, or default to USD
      const sdkCurrency = this.currency || 'USD'

      // Check if SDK is already loaded with the correct currency
      if (window.paypal && this.loadedSDKCurrency === sdkCurrency) {
        console.log('✅ [PAYPAL_SDK] PayPal SDK already loaded with correct currency:', sdkCurrency)
        return
      }

      // If SDK is loaded but with wrong currency, warn and continue
      if (window.paypal && this.loadedSDKCurrency !== sdkCurrency) {
        console.warn('⚠️ [PAYPAL_SDK] PayPal SDK already loaded but with different currency!')
        console.warn('⚠️ [PAYPAL_SDK] Loaded:', this.loadedSDKCurrency, '| Needed:', sdkCurrency)
        console.warn('⚠️ [PAYPAL_SDK] Currency change will be handled by watcher')
        return
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement('script')

        // IMPORTANT: To support multiple currencies, use the currency parameter with your intended currencies
        // Format: currency=USD,CAD,EUR (comma-separated list)
        // Or just use the specific currency needed for this checkout session
        script.src = `https://www.paypal.com/sdk/js?client-id=${this.getPayPalClientId()}&currency=${sdkCurrency}&components=buttons,funding-eligibility,googlepay,applepay&disable-funding=credit,card`

        console.log('📦 [PAYPAL_SDK] Loading PayPal SDK with currency:', sdkCurrency)
        console.log('📦 [PAYPAL_SDK] Full SDK URL:', script.src)

        script.onload = () => {
          this.loadedSDKCurrency = sdkCurrency  // Track loaded currency
          console.log('✅ [PAYPAL_SDK] PayPal SDK loaded successfully for currency:', sdkCurrency)
          resolve()
        }
        script.onerror = () => {
          console.error('❌ [PAYPAL_SDK] Failed to load PayPal SDK')
          reject(new Error('Failed to load PayPal SDK'))
        }
        document.head.appendChild(script)
      })
    },

    async loadGooglePaySDK() {
      if (window.google && window.google.payments) {
        console.log('Google Pay SDK already loaded')
        return
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://pay.google.com/gp/p/js/pay.js'
        script.onload = () => {
          console.log('Google Pay SDK loaded successfully')
          resolve()
        }
        script.onerror = () => {
          console.error('Failed to load Google Pay SDK')
          resolve() // Don't reject, just continue without native Google Pay
        }
        document.head.appendChild(script)
      })
    },

    getPayPalClientId() {
      // Replace with your actual PayPal client ID
      return process.env.VUE_APP_PAYPAL_CLIENT_ID || 'AXC8QA5oKN2FYmkENzVMFZaMbO1Nb4dJlIVJwRSIMLZwmKxYqJ8G-GIzglfWlQn8Hqm2VGDbZ7_ZqgKN'
    },

    attachEventListeners() {
      console.log('🔍 UnifiedExpressCheckout: Attempting to attach event listeners...');
      
      // Find and attach listeners to existing payment buttons
      const paypalBtn = document.querySelector('.payment-button.paypal')
      const gpayBtn = document.querySelector('.payment-button.gpay')
      const applePayBtn = document.querySelector('.payment-button.apple-pay')

      console.log('🔍 Found PayPal button:', paypalBtn);
      console.log('🔍 Found GPay button:', gpayBtn);
      console.log('🔍 Found Apple Pay button:', applePayBtn);

      if (paypalBtn) {
        console.log('✅ Attaching PayPal listener');
        paypalBtn.addEventListener('click', this.handlePayPalClick)
        this.paypalButton = paypalBtn
      }

      if (gpayBtn) {
        console.log('✅ Attaching GPay listener');
        gpayBtn.addEventListener('click', this.handleGpayClick)
        this.gpayButton = gpayBtn
        if (!this.isGooglePayAvailable) {
          gpayBtn.disabled = true
          gpayBtn.style.opacity = '0.5'
          gpayBtn.title = 'Google Pay is not available on this device'
        }
      }

      if (applePayBtn) {
        console.log('✅ Attaching Apple Pay listener');
        applePayBtn.addEventListener('click', this.handleApplePayClick)
        this.applePayButton = applePayBtn
        if (!this.isApplePayAvailable) {
          applePayBtn.disabled = true
          applePayBtn.style.opacity = '0.5'
          applePayBtn.title = 'Apple Pay is not available on this device'
        }
      }
    },

    removeEventListeners() {
      if (this.paypalButton) {
        this.paypalButton.removeEventListener('click', this.handlePayPalClick)
      }
      if (this.gpayButton) {
        this.gpayButton.removeEventListener('click', this.handleGpayClick)
      }
      if (this.applePayButton) {
        this.applePayButton.removeEventListener('click', this.handleApplePayClick)
      }
    },

    handlePayPalClick(event) {
      console.log('🎯 PayPal button clicked!', event);
      event.preventDefault()
      event.stopPropagation()
      if (!this.isLoading && this.canCheckout) {
        console.log('✅ Initiating PayPal payment...');
        this.initiatePayPalPayment()
      } else {
        console.log('❌ Cannot proceed - isLoading:', this.isLoading, 'canCheckout:', this.canCheckout);
      }
    },

    handleGpayClick(event) {
      console.log('🎯 Google Pay button clicked!', event);
      event.preventDefault()
      event.stopPropagation()
      if (!this.isLoading && this.canCheckout && this.isGooglePayAvailable) {
        console.log('✅ Initiating Google Pay payment...');
        this.initiateGooglePayPayment()
      } else {
        console.log('❌ Cannot proceed - isLoading:', this.isLoading, 'canCheckout:', this.canCheckout, 'isGooglePayAvailable:', this.isGooglePayAvailable);
        if (!this.isGooglePayAvailable) {
          this.error = 'Google Pay is not available on this device or browser'
        }
      }
    },

    handleApplePayClick(event) {
      console.log('🎯 Apple Pay button clicked!', event);
      event.preventDefault()
      event.stopPropagation()
      if (!this.isLoading && this.canCheckout && this.isApplePayAvailable) {
        console.log('✅ Initiating Apple Pay payment...');
        this.initiateApplePayPayment()
      } else {
        console.log('❌ Cannot proceed - isLoading:', this.isLoading, 'canCheckout:', this.canCheckout, 'isApplePayAvailable:', this.isApplePayAvailable);
        if (!this.isApplePayAvailable) {
          this.error = 'Apple Pay is not available on this device or browser'
        }
      }
    },

    async initiatePayPalPayment() {
      try {
        console.log('🎬 [PAYPAL_INIT] === STARTING EXPRESS CHECKOUT PAYPAL FLOW ===');
        console.log('🎬 [PAYPAL_INIT] Timestamp:', new Date().toISOString());

        this.currentMethod = 'paypal'
        this.isLoading = true
        this.error = null
        this.updateButtonStates()

        // STEP 1: Create express checkout session
        console.log('📋 [PAYPAL_INIT] Step 1: Creating express checkout session...');
        const checkoutResponse = await this.createExpressCheckoutSession('paypal')

        if (!checkoutResponse.success) {
          throw new Error(checkoutResponse.message || 'Failed to create checkout session')
        }

        this.checkoutId = checkoutResponse.checkout_id
        console.log('✅ [PAYPAL_INIT] Checkout session created. ID:', this.checkoutId);

        // STEP 2: Create order in database IMMEDIATELY (BEFORE showing PayPal popup)
        console.log('📦 [PAYPAL_INIT] Step 2: Creating order in database BEFORE PayPal popup...');
        const orderResponse = await this.createOrderInDatabase()

        if (!orderResponse.success) {
          throw new Error(orderResponse.message || 'Failed to create order in database')
        }

        this.orderId = orderResponse.order_id
        console.log('✅ [PAYPAL_INIT] Order created in database. Order ID:', this.orderId);
        console.log('✅ [PAYPAL_INIT] Order status: PENDING (awaiting PayPal capture)');

        // STEP 3: Show PayPal popup and initialize buttons
        console.log('💳 [PAYPAL_INIT] Step 3: Showing PayPal popup and initializing buttons...');
        console.log('💳 [PAYPAL_INIT] PayPal popup currency:', this.currency);
        console.log('💳 [PAYPAL_INIT] Amount breakdown:', {
          currency: this.currency,
          itemTotal: this.amount,
          shipping: this.dynamicShippingAmount,
          tax: this.dynamicTaxAmount,
          shippingTax: this.dynamicShippingTaxAmount,
          total: this.dynamicTotalAmount
        });

        this.showPayPalPopup = true
        await this.$nextTick() // Wait for popup to render
        await this.initializePayPalButtons()

        console.log('✅ [PAYPAL_INIT] PayPal buttons initialized. Waiting for customer approval...');

      } catch (error) {
        console.error('❌ [PAYPAL_INIT] Fatal error during initialization:', error);
        console.error('❌ [PAYPAL_INIT] Error stack:', error.stack);
        this.error = error.message || 'Failed to initialize PayPal payment'
        console.error('PayPal initialization error:', error)
      } finally {
        this.isLoading = false
        this.updateButtonStates()
      }
    },

    async initializePayPalButtons() {
      if (!window.paypal) {
        throw new Error('PayPal SDK not loaded')
      }

      const container = document.getElementById('paypal-popup-container')
      if (!container) {
        throw new Error('PayPal popup container not found')
      }

      // Clear existing buttons
      container.innerHTML = ''

      // Calculate item total
      const itemTotal = this.items.reduce((total, item) => {
        return total + (item.price * item.quantity)
      }, 0)

      const paypalButtons = window.paypal.Buttons({
        style: {
          shape: 'rect',
          color: 'gold',
          layout: 'vertical',
          label: 'paypal',
          height: 50
        },

        createOrder: (data, actions) => {
          console.log('💳 [PAYPAL_CREATE_ORDER] Creating PayPal order')
          console.log('💳 [PAYPAL_CREATE_ORDER] Item total:', itemTotal)

          // Start with product-only price (no shipping/tax yet)
          return actions.order.create({
            purchase_units: [{
              amount: {
                currency_code: this.currency,
                value: itemTotal.toFixed(2),  // Products only initially
                breakdown: {
                  item_total: {
                    currency_code: this.currency,
                    value: itemTotal.toFixed(2)
                  }
                }
              },
              description: `Order from ${this.merchantName}`,
              custom_id: this.checkoutId,
              items: this.items.map(item => ({
                name: item.name,
                unit_amount: {
                  currency_code: this.currency,
                  value: item.price.toFixed(2)
                },
                quantity: item.quantity.toString(),
                category: 'PHYSICAL_GOODS'
              }))
            }],
            application_context: {
              brand_name: this.merchantName,
              landing_page: 'BILLING',
              user_action: 'PAY_NOW',
              shipping_preference: 'GET_FROM_FILE'  // PayPal collects address
            }
          })
        },

        // 🔥 NEW: Calculate shipping/tax when address is entered (BEFORE payment capture)
        onShippingChange: async (data, actions) => {
          console.log('🚚 [PAYPAL_SHIPPING_CHANGE] PayPal onShippingChange callback triggered!')
          console.log('🚚 [PAYPAL_SHIPPING_CHANGE] Shipping address data:', data.shipping_address)

          try {
            // Extract shipping address from PayPal callback
            // NOTE: onShippingChange only provides partial address (city, state, postal_code, country)
            // Full address details (name, street address) come later in onApprove
            const shippingAddress = {
              first_name: data.shipping_address.recipient_name?.split(' ')[0] || 'Express',
              last_name: data.shipping_address.recipient_name?.split(' ').slice(1).join(' ') || 'Customer',
              email: 'temporary@express.checkout', // Placeholder - not available yet
              phone: '', // Not available in shipping change callback
              address: data.shipping_address.line1 || '123 Temporary St', // Placeholder
              address2: data.shipping_address.line2 || '',
              city: data.shipping_address.city || '',
              state: data.shipping_address.state || '',
              postal_code: data.shipping_address.postal_code || '',
              country: data.shipping_address.country_code || 'US'
            }

            console.log('📦 [PAYPAL_SHIPPING_CHANGE] Formatted shipping address:', shippingAddress)
            console.log('🌐 [PAYPAL_SHIPPING_CHANGE] Calling backend to calculate shipping/tax...')

            // Call backend to calculate shipping and tax
            const response = await axios.post(this.baseURL + 'express-checkout/confirm-address', {
              checkout_id: this.checkoutId,
              shipping_address: shippingAddress,
              billing_address: shippingAddress,
              is_shipping_calculation_only: true  // Flag to indicate this is just for shipping/tax calculation
            })

            console.log('✅ [PAYPAL_SHIPPING_CHANGE] Backend response:', response.data)

            if (!response.data.success) {
              console.error('❌ [PAYPAL_SHIPPING_CHANGE] Backend returned error:', response.data.message)
              return actions.reject()
            }

            // Update dynamic amounts from backend response
            this.dynamicShippingAmount = response.data.shipping_amount || 0
            this.dynamicTaxAmount = response.data.tax_amount || 0
            this.dynamicShippingTaxAmount = response.data.shipping_tax_amount || 0

            console.log('💰 [PAYPAL_SHIPPING_CHANGE] Updated amounts:', {
              shipping: this.dynamicShippingAmount,
              tax: this.dynamicTaxAmount,
              shippingTax: this.dynamicShippingTaxAmount
            })

            // Calculate total tax (product tax + shipping tax)
            const totalTax = this.dynamicTaxAmount + this.dynamicShippingTaxAmount

            // Calculate new total
            const newTotal = itemTotal + this.dynamicShippingAmount + totalTax

            console.log('💰 [PAYPAL_SHIPPING_CHANGE] New total:', newTotal)
            console.log('📝 [PAYPAL_SHIPPING_CHANGE] Breakdown:', {
              itemTotal,
              shipping: this.dynamicShippingAmount,
              tax: totalTax,
              total: newTotal
            })

            // Update PayPal order with new amounts BEFORE capture
            console.log('🔄 [PAYPAL_SHIPPING_CHANGE] Updating PayPal order with new amounts...')
            return actions.order.patch([{
              op: 'replace',
              path: "/purchase_units/@reference_id=='default'/amount",
              value: {
                currency_code: this.currency,
                value: newTotal.toFixed(2),
                breakdown: {
                  item_total: {
                    currency_code: this.currency,
                    value: itemTotal.toFixed(2)
                  },
                  shipping: {
                    currency_code: this.currency,
                    value: this.dynamicShippingAmount.toFixed(2)
                  },
                  tax_total: {
                    currency_code: this.currency,
                    value: totalTax.toFixed(2)
                  }
                }
              }
            }])
            .then(() => {
              console.log('✅ [PAYPAL_SHIPPING_CHANGE] PayPal order updated successfully')
            })
          } catch (error) {
            console.error('❌ [PAYPAL_SHIPPING_CHANGE] Failed to calculate shipping/tax:', error)
            console.error('❌ [PAYPAL_SHIPPING_CHANGE] Error details:', error.response?.data)
            return actions.reject()  // Reject order if calculation fails
          }
        },

        onApprove: async (data, actions) => {
          console.log('🎉 [PAYPAL_APPROVE] === CUSTOMER APPROVED PAYMENT ===')
          console.log('🎉 [PAYPAL_APPROVE] Timestamp:', new Date().toISOString())
          console.log('🎉 [PAYPAL_APPROVE] PayPal Order ID:', data.orderID)
          console.log('🎉 [PAYPAL_APPROVE] Payer ID:', data.payerID)
          console.log('🎉 [PAYPAL_APPROVE] Our Order ID:', this.orderId)

          this.isProcessing = true

          try {
            // STEP 1: Capture PayPal payment
            console.log('💰 [PAYPAL_APPROVE] Step 1: Capturing PayPal payment...')
            const captureStartTime = Date.now()
            const details = await actions.order.capture()
            const captureDuration = Date.now() - captureStartTime

            console.log('✅ [PAYPAL_APPROVE] PayPal payment captured successfully!')
            console.log('✅ [PAYPAL_APPROVE] Capture took:', captureDuration, 'ms')
            console.log('✅ [PAYPAL_APPROVE] PayPal capture details:', details)
            console.log('✅ [PAYPAL_APPROVE] Capture status:', details.status)
            console.log('✅ [PAYPAL_APPROVE] Payment amount:', details.purchase_units[0]?.amount)

            // STEP 2: Extract shipping address from PayPal response
            console.log('📋 [PAYPAL_APPROVE] Step 2: Extracting shipping address from PayPal...')
            const shippingAddress = this.extractPayPalShippingAddress(details)
            console.log('✅ [PAYPAL_APPROVE] Shipping address extracted:', shippingAddress)

            // Store collected address
            this.collectedShippingAddress = shippingAddress

            // Close PayPal popup
            this.closePaymentPopup()

            // STEP 3: Update existing order with PayPal payment details
            console.log('🔄 [PAYPAL_APPROVE] Step 3: Updating order with PayPal payment...')
            console.log('🔄 [PAYPAL_APPROVE] Order ID to update:', this.orderId)
            console.log('🔄 [PAYPAL_APPROVE] PayPal transaction ID:', details.id)

            await this.updateOrderWithPayment({
              order_id: this.orderId,
              paypal_order_id: data.orderID,
              paypal_transaction_id: details.id,
              payer_id: data.payerID,
              payment_status: 'completed',
              shipping_address: shippingAddress,
              capture_details: details
            })

            console.log('✅ [PAYPAL_APPROVE] === PAYMENT FLOW COMPLETED SUCCESSFULLY ===')
            console.log('✅ [PAYPAL_APPROVE] Final Order ID:', this.orderId)
            console.log('✅ [PAYPAL_APPROVE] PayPal Transaction ID:', details.id)

            // Emit success event
            this.handlePaymentSuccess({
              order_id: this.orderId,
              transaction_id: details.id
            })

          } catch (error) {
            console.error('❌ [PAYPAL_APPROVE] === PAYMENT CAPTURE FAILED ===')
            console.error('❌ [PAYPAL_APPROVE] Error:', error)
            console.error('❌ [PAYPAL_APPROVE] Error message:', error.message)
            console.error('❌ [PAYPAL_APPROVE] Error stack:', error.stack)
            console.error('❌ [PAYPAL_APPROVE] Order ID (will remain as pending):', this.orderId)
            this.handlePaymentError('Payment processing failed: ' + error.message)
          }
        },

        onError: (err) => {
          console.error('❌ [PAYPAL_ERROR] === PAYPAL ERROR OCCURRED ===')
          console.error('❌ [PAYPAL_ERROR] Timestamp:', new Date().toISOString())
          console.error('❌ [PAYPAL_ERROR] Error object:', err)
          console.error('❌ [PAYPAL_ERROR] Error message:', err.message)
          console.error('❌ [PAYPAL_ERROR] Error name:', err.name)
          console.error('❌ [PAYPAL_ERROR] Our Order ID (will remain as pending):', this.orderId)

          // Track error in analytics
          this.trackCheckoutEvent('paypal_error', {
            order_id: this.orderId,
            error_message: err.message,
            error_name: err.name
          })

          this.closePaymentPopup()
          this.handlePaymentError('PayPal payment failed: ' + (err.message || 'Unknown error'))
        },

        onCancel: (data) => {
          console.log('⚠️ [PAYPAL_CANCEL] === CUSTOMER CANCELLED PAYMENT ===')
          console.log('⚠️ [PAYPAL_CANCEL] Timestamp:', new Date().toISOString())
          console.log('⚠️ [PAYPAL_CANCEL] PayPal data:', data)
          console.log('⚠️ [PAYPAL_CANCEL] Our Order ID (will remain as pending):', this.orderId)
          console.log('⚠️ [PAYPAL_CANCEL] Order can still be completed later')

          // Track cancellation in analytics
          this.trackCheckoutEvent('paypal_cancelled', {
            order_id: this.orderId,
            checkout_id: this.checkoutId
          })

          this.closePaymentPopup()
          this.handlePaymentError('Payment was cancelled. Your order has been saved and you can complete it later.')
        }
      })

      if (paypalButtons.isEligible()) {
        await paypalButtons.render('#paypal-popup-container')
        console.log('✅ PayPal buttons rendered successfully in popup')
      } else {
        throw new Error('PayPal buttons not eligible')
      }
    },

    closePaymentPopup() {
      this.showPayPalPopup = false
      this.isProcessing = false
    },

    async initiateGooglePayPayment() {
      try {
        this.currentMethod = 'google_pay_via_paypal'
        this.isLoading = true
        this.error = null
        this.updateButtonStates()

        // Create express checkout session
        const checkoutResponse = await this.createExpressCheckoutSession('google_pay_via_paypal')
        
        if (!checkoutResponse.success) {
          throw new Error(checkoutResponse.message || 'Failed to create checkout session')
        }

        this.checkoutId = checkoutResponse.checkout_id

        // Initialize Google Pay via PayPal
        await this.initializeGooglePayViaPayPal()

      } catch (error) {
        this.error = error.message || 'Failed to initialize Google Pay payment'
        console.error('Google Pay initialization error:', error)
      } finally {
        this.isLoading = false
        this.updateButtonStates()
      }
    },

    async initializeGooglePayViaPayPal() {
      if (!this.googlePayClient) {
        throw new Error('Google Pay not available')
      }

      try {
        this.isProcessing = true

        // Check if we're using native Google Pay or PayPal's Google Pay
        if (this.googlePayClient.loadPaymentData) {
          // Native Google Pay API
          console.log('💳 [GOOGLE_PAY] Using native Google Pay API...')

          // Calculate item total
          const itemTotal = this.items.reduce((total, item) => {
            return total + (item.price * item.quantity)
          }, 0)

          // Use DIRECT tokenization (merchant processes on backend)
          const paymentDataRequest = {
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [{
              type: 'CARD',
              parameters: {
                allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER']
              },
              tokenizationSpecification: {
                type: 'DIRECT',
                parameters: {
                  protocolVersion: 'ECv2',
                  publicKey: 'BDXfdhMHTB+j/ztj3s3H4jfj/ksD+fj/kD3+sH3k4jfj3s3H4jfj/ksD+fj/kD3+s='
                }
              }
            }],
            merchantInfo: {
              merchantName: this.merchantName || 'CYMCORP'
            },
            transactionInfo: {
              totalPriceStatus: 'ESTIMATED',
              totalPrice: itemTotal.toFixed(2),
              currencyCode: this.currency,
              countryCode: 'US',
              displayItems: [
                {
                  label: 'Subtotal',
                  type: 'SUBTOTAL',
                  price: itemTotal.toFixed(2)
                }
              ]
            },
            shippingAddressRequired: true,
            shippingAddressParameters: {
              phoneNumberRequired: true,
              allowedCountryCodes: ['US', 'CA']
            },
            emailRequired: true
          }

          console.log('🔍 [GOOGLE_PAY] Simple payment request created')

          // Load payment data WITHOUT callbacks
          const result = await this.googlePayClient.loadPaymentData(paymentDataRequest)

          console.log('✅ [GOOGLE_PAY] Payment data received:', result)

          // Validate result before processing
          if (!result) {
            throw new Error('Payment data is empty - user may have cancelled')
          }

          if (!result.paymentMethodData || !result.paymentMethodData.tokenizationData) {
            throw new Error('Invalid payment data structure received from Google Pay')
          }

          // Extract shipping address from Google Pay response
          const shippingAddress = this.extractGooglePayShippingAddress(result)
          console.log('📋 [GOOGLE_PAY] Shipping address extracted:', shippingAddress)

          // Store collected address
          this.collectedShippingAddress = shippingAddress

          // Confirm address with backend to calculate shipping and tax
          if (shippingAddress && this.hasCompleteAddress(shippingAddress)) {
            console.log('🌐 [GOOGLE_PAY] Confirming address with backend...')
            await this.confirmAddressWithBackend(shippingAddress)
            console.log('💰 [GOOGLE_PAY] After address confirmation - Dynamic amounts:', {
              shipping: this.dynamicShippingAmount,
              tax: this.dynamicTaxAmount,
              shippingTax: this.dynamicShippingTaxAmount,
              total: this.dynamicTotalAmount
            })
          } else {
            console.warn('⚠️ [GOOGLE_PAY] Address is incomplete, skipping backend confirmation')
          }

          // Process payment
          console.log('🚀 [GOOGLE_PAY] Processing payment with checkout ID:', this.checkoutId)
          console.log('💰 [GOOGLE_PAY] Final amounts before payment:', {
            dynamicShippingAmount: this.dynamicShippingAmount,
            dynamicTaxAmount: this.dynamicTaxAmount,
            dynamicTotalAmount: this.dynamicTotalAmount
          })
          await this.processPayment('google_pay_via_paypal', result.paymentMethodData.tokenizationData.token, {
            paymentData: result
          })

          console.log('✅ [GOOGLE_PAY] Payment processing completed!')

        } else {
          // PayPal Google Pay component (recommended for PayPal SDK)
          console.log('💳 [GOOGLE_PAY] Using PayPal Google Pay component...')

          // Calculate item total
          const itemTotal = this.items.reduce((total, item) => {
            return total + (item.price * item.quantity)
          }, 0)

          const paymentRequest = {
            merchantInfo: {
              merchantName: this.merchantName
            },
            transactionInfo: {
              totalPriceStatus: 'FINAL',
              totalPrice: itemTotal.toFixed(2),  // Use product price only initially
              currencyCode: this.currency
            }
          }

          console.log('🔍 [GOOGLE_PAY] Payment request:', paymentRequest)

          const result = await this.googlePayClient.confirmOrder({
            paymentRequest
          })

          console.log('✅ [GOOGLE_PAY] PayPal Google Pay result:', result)

          // Extract shipping address from Google Pay response
          const shippingAddress = this.extractGooglePayShippingAddress(result)
          console.log('📋 [GOOGLE_PAY] Shipping address extracted:', shippingAddress)

          // Store collected address
          this.collectedShippingAddress = shippingAddress

          // Confirm address with backend to calculate shipping and tax
          if (shippingAddress && this.hasCompleteAddress(shippingAddress)) {
            console.log('🌐 [GOOGLE_PAY] Confirming address with backend...')
            await this.confirmAddressWithBackend(shippingAddress)
            console.log('💰 [GOOGLE_PAY] After address confirmation - Dynamic amounts:', {
              shipping: this.dynamicShippingAmount,
              tax: this.dynamicTaxAmount,
              shippingTax: this.dynamicShippingTaxAmount,
              total: this.dynamicTotalAmount
            })
          } else {
            console.warn('⚠️ [GOOGLE_PAY] Address is incomplete, skipping backend confirmation')
          }

          // Process payment
          console.log('🚀 [GOOGLE_PAY] Processing payment with checkout ID:', this.checkoutId)
          console.log('💰 [GOOGLE_PAY] Final amounts before payment:', {
            dynamicShippingAmount: this.dynamicShippingAmount,
            dynamicTaxAmount: this.dynamicTaxAmount,
            dynamicTotalAmount: this.dynamicTotalAmount
          })
          await this.processPayment('google_pay_via_paypal', result.paymentData, {
            orderID: result.orderID
          })

          console.log('✅ [GOOGLE_PAY] Payment processing completed!')
        }
      } catch (error) {
        console.error('❌ [GOOGLE_PAY] Payment error:', error)

        // Handle user cancellation gracefully (Google Pay returns error without message when user closes the sheet)
        if (error && error.statusCode === 'CANCELED') {
          console.log('ℹ️ [GOOGLE_PAY] User cancelled payment')
          this.error = null  // Don't show error for cancellation
        } else if (!error || !error.message || error.message === 'undefined') {
          console.log('ℹ️ [GOOGLE_PAY] Payment sheet closed or cancelled')
          this.error = null  // Don't show error for cancellation
        } else {
          this.handlePaymentError('Google Pay payment failed: ' + error.message)
        }
      } finally {
        this.isProcessing = false
      }
    },

    async initiateApplePayPayment() {
      try {
        this.currentMethod = 'apple_pay_via_paypal'
        this.isLoading = true
        this.error = null
        this.updateButtonStates()

        // Create express checkout session
        const checkoutResponse = await this.createExpressCheckoutSession('apple_pay_via_paypal')
        
        if (!checkoutResponse.success) {
          throw new Error(checkoutResponse.message || 'Failed to create checkout session')
        }

        this.checkoutId = checkoutResponse.checkout_id

        // Initialize Apple Pay via PayPal
        await this.initiateApplePayViaPayPal()

      } catch (error) {
        this.error = error.message || 'Failed to initialize Apple Pay payment'
        console.error('Apple Pay initialization error:', error)
      } finally {
        this.isLoading = false
        this.updateButtonStates()
      }
    },

    async initiateApplePayViaPayPal() {
      if (!window.paypal || !window.paypal.Applepay) {
        throw new Error('Apple Pay via PayPal not available')
      }

      try {
        this.isProcessing = true

        console.log('💳 [APPLE_PAY] Initializing Apple Pay via PayPal...')

        const applePayComponent = window.paypal.Applepay()

        // Calculate item total
        const itemTotal = this.items.reduce((total, item) => {
          return total + (item.price * item.quantity)
        }, 0)

        console.log('💳 [APPLE_PAY] Item total:', itemTotal)

        // Create payment request with shipping callbacks
        const paymentRequest = {
          countryCode: 'US',
          currencyCode: this.currency,
          requiredShippingContactFields: ['postalAddress', 'phone', 'email'],
          total: {
            label: this.merchantName,
            amount: itemTotal.toFixed(2),  // Start with product price only
            type: 'pending'  // Pending until shipping address is selected
          },
          lineItems: this.items.map(item => ({
            label: item.name,
            amount: (item.price * item.quantity).toFixed(2),
            type: 'final'
          }))
        }

        // 🔥 NEW: Add shipping contact selection handler
        const onShippingContactSelected = async (shippingContact) => {
          console.log('🚚 [APPLE_PAY_SHIPPING_CHANGE] Shipping contact selected:', shippingContact)

          try {
            // Extract shipping address from Apple Pay callback
            const formattedAddress = {
              first_name: shippingContact.givenName || '',
              last_name: shippingContact.familyName || '',
              email: shippingContact.emailAddress || '',
              phone: shippingContact.phoneNumber || '',
              address: shippingContact.addressLines?.[0] || '',
              address2: shippingContact.addressLines?.[1] || '',
              city: shippingContact.locality || '',
              state: shippingContact.administrativeArea || '',
              postal_code: shippingContact.postalCode || '',
              country: shippingContact.countryCode || 'US'
            }

            console.log('📦 [APPLE_PAY_SHIPPING_CHANGE] Formatted address:', formattedAddress)
            console.log('🌐 [APPLE_PAY_SHIPPING_CHANGE] Calling backend to calculate shipping/tax...')

            // Call backend to calculate shipping and tax
            const response = await axios.post(this.baseURL + 'express-checkout/confirm-address', {
              checkout_id: this.checkoutId,
              shipping_address: formattedAddress,
              billing_address: formattedAddress
            })

            console.log('✅ [APPLE_PAY_SHIPPING_CHANGE] Backend response:', response.data)

            if (!response.data.success) {
              console.error('❌ [APPLE_PAY_SHIPPING_CHANGE] Backend error:', response.data.message)
              return {
                errors: [{
                  code: 'shippingContactInvalid',
                  contactField: 'postalAddress',
                  message: 'Cannot calculate shipping for this address'
                }]
              }
            }

            // Update dynamic amounts
            this.dynamicShippingAmount = response.data.shipping_amount || 0
            this.dynamicTaxAmount = response.data.tax_amount || 0
            this.dynamicShippingTaxAmount = response.data.shipping_tax_amount || 0

            console.log('💰 [APPLE_PAY_SHIPPING_CHANGE] Updated amounts:', {
              shipping: this.dynamicShippingAmount,
              tax: this.dynamicTaxAmount,
              shippingTax: this.dynamicShippingTaxAmount
            })

            const totalTax = this.dynamicTaxAmount + this.dynamicShippingTaxAmount
            const newTotal = itemTotal + this.dynamicShippingAmount + totalTax

            console.log('💰 [APPLE_PAY_SHIPPING_CHANGE] New total:', newTotal)

            // Return updated payment details
            return {
              newTotal: {
                label: this.merchantName,
                amount: newTotal.toFixed(2),
                type: 'final'
              },
              newLineItems: [
                ...this.items.map(item => ({
                  label: item.name,
                  amount: (item.price * item.quantity).toFixed(2),
                  type: 'final'
                })),
                {
                  label: 'Shipping',
                  amount: this.dynamicShippingAmount.toFixed(2),
                  type: 'final'
                },
                {
                  label: 'Tax',
                  amount: totalTax.toFixed(2),
                  type: 'final'
                }
              ]
            }
          } catch (error) {
            console.error('❌ [APPLE_PAY_SHIPPING_CHANGE] Error calculating shipping/tax:', error)
            return {
              errors: [{
                code: 'shippingContactInvalid',
                contactField: 'postalAddress',
                message: 'Failed to calculate shipping and tax'
              }]
            }
          }
        }

        // Configure Apple Pay with shipping callback
        const applePayConfig = {
          paymentRequest,
          onShippingContactSelected
        }

        console.log('🚀 [APPLE_PAY] Confirming Apple Pay order...')
        const result = await applePayComponent.confirmOrder(applePayConfig)

        console.log('✅ [APPLE_PAY] Apple Pay payment authorized:', result)

        // Extract shipping address from Apple Pay response
        const shippingAddress = this.extractApplePayShippingAddress(result)

        console.log('📋 [APPLE_PAY] Shipping address extracted:', shippingAddress)

        // Store collected address
        this.collectedShippingAddress = shippingAddress

        // Process payment immediately (amounts already calculated via callback)
        console.log('🚀 [APPLE_PAY] Processing payment...')
        await this.processPayment('apple_pay_via_paypal', result.paymentData, {
          orderID: result.orderID
        })

        console.log('✅ [APPLE_PAY] Payment processing completed!')

      } catch (error) {
        console.error('❌ [APPLE_PAY] Payment error:', error)
        this.handlePaymentError('Apple Pay payment failed: ' + error.message)
      } finally {
        this.isProcessing = false
      }
    },

    async createExpressCheckoutSession(paymentMethod) {
      try {
        const cartItems = this.items.map(item => ({
          id: item.id,
          product_id: item.id,
          quantity: item.quantity || 1,
          price: item.price,
          name: item.name || `Product ${item.id}`
        }))

        console.log('🔍 [CREATE_SESSION] Creating express checkout session:', {
          cart_items: cartItems,
          currency: this.currency,
          payment_method: paymentMethod,
          timestamp: new Date().toISOString()
        });

        const response = await axios.post(this.baseURL + 'express-checkout/create', {
          cart_items: cartItems,
          currency: this.currency,
          locale: 'en-US',
          auto_redirect: false,
          skip_shop_pay: false,
          customer_data: this.getCustomerData(),
          shipping_data: this.getShippingData(),
          shipping_address: this.getShippingData(), // Add this line
          payment_method: paymentMethod,
          user_id: (this.userLogin.checkIsLoggedIn && this.userLogin.getUserUserData)? this.userLogin.getUserUserData.id : null ,
          referral_id: this.userLogin.getReferalId
        })

        // Update dynamic amounts from response
        this.dynamicShippingAmount = response.data.shipping_amount || 0;
        this.dynamicTaxAmount = response.data.tax_amount || 0;
        this.dynamicShippingTaxAmount = response.data.shipping_tax_amount || 0;
        this.dynamicTotalAmount = response.data.total_amount || this.amount;

        console.log('✅ [CREATE_SESSION] Express checkout session created successfully:', response.data);

        return response.data
      } catch (error) {
        console.error('❌ [CREATE_SESSION] Failed to create express checkout session:', error)
        console.error('❌ [CREATE_SESSION] Error response:', error.response?.data)
        throw new Error(error.response?.data?.message || 'Failed to create checkout session')
      }
    },

    async createOrderInDatabase() {
      try {
        console.log('🔨 [CREATE_ORDER] Creating order in database...');
        console.log('🔨 [CREATE_ORDER] Timestamp:', new Date().toISOString());
        console.log('🔨 [CREATE_ORDER] Checkout ID:', this.checkoutId);

        const cartItems = this.items.map(item => ({
          product_id: item.id,
          quantity: item.quantity || 1,
          price: item.price,
          autoship: 'no' // Express checkout doesn't support autoship
        }))

        // Get customer/shipping data
        const shippingData = this.collectedShippingAddress || this.getShippingData()

        console.log('🔨 [CREATE_ORDER] Cart items:', cartItems);
        console.log('🔨 [CREATE_ORDER] Shipping data:', shippingData);

        // Convert country code to numeric ID
        let countryId = 231; // Default to US (231)
        if (shippingData.country) {
          if (typeof shippingData.country === 'number') {
            countryId = shippingData.country;
          } else if (shippingData.country === 'CA' || shippingData.country === 'Canada') {
            countryId = 38; // Canada
          } else if (shippingData.country === 'US' || shippingData.country === 'United States') {
            countryId = 231; // United States
          }
        } else if (this.country) {
          countryId = this.country;
        } else if (this.currency === 'CAD') {
          countryId = 38; // Canada
        }

        // Convert state to numeric ID or use default
        let stateId = 1; // Default state
        if (shippingData.state && typeof shippingData.state === 'number') {
          stateId = shippingData.state;
        } else if (shippingData.state && typeof shippingData.state === 'string') {
          // For now, use default. In future, could map state codes to IDs
          stateId = 1;
        }

        console.log('🔢 [CREATE_ORDER] Country ID:', countryId, 'State ID:', stateId);

        const orderPayload = {
          checkout_id: this.checkoutId,
          referal_id: this.userLogin.getReferalId,
          user_id: (this.userLogin.checkIsLoggedIn && this.userLogin.getUserUserData) ? this.userLogin.getUserUserData.id : null,
          email: shippingData.email || 'express@checkout.pending',
          first_name: shippingData.first_name || 'Express',
          last_name: shippingData.last_name || 'Customer',
          mobile: shippingData.phone || '0000000000', // Backend requires mobile, provide default
          order_date: new Date(),
          completion_date: null,
          shipping_status: 'Not Shipped',
          order_status: 'Pending', // Will be updated after PayPal capture
          payment_status: 'pending_capture', // Custom status to track PayPal flow
          total: this.amount + this.dynamicShippingAmount + this.dynamicTaxAmount + this.dynamicShippingTaxAmount,
          netTotal: this.amount + this.dynamicShippingAmount + this.dynamicTaxAmount + this.dynamicShippingTaxAmount,
          currency: this.currency,
          ship_address: shippingData.address || 'Pending from PayPal',
          ship_address2: shippingData.address2 || '',
          ship_city: shippingData.city || 'Pending',
          country: countryId, // Must be numeric ID
          state: stateId, // Must be numeric ID
          ship_zipcode: shippingData.postal_code || '00000',
          shipping_method: 'Express Checkout',
          shipping_amount: this.dynamicShippingAmount,
          shipping_tax_amount: this.dynamicShippingTaxAmount,
          discount_amount: 0,
          discount_coupan_id: '', // Backend expects this field (note the typo "coupan")
          tax_amount: this.dynamicTaxAmount,
          notes: `Express checkout initiated via PayPal at ${new Date().toISOString()}`,
          products: cartItems,
          shopping_country_code: this.currency === 'CAD' ? 'CA' : 'US',
          payment_method: 'paypal'
        }

        console.log('📤 [CREATE_ORDER] Sending order payload to API:', orderPayload);

        const apiUrl = this.userLogin.checkIsLoggedIn
          ? this.baseURL + 'orders'
          : this.baseURL + 'guest/orders'

        console.log('📤 [CREATE_ORDER] API URL:', apiUrl);

        const response = await axios.post(apiUrl, orderPayload)

        console.log('✅ [CREATE_ORDER] Order created successfully!');
        console.log('✅ [CREATE_ORDER] Response:', response.data);

        if (response.data.error) {
          throw new Error(response.data.error)
        }

        // Track order creation in analytics
        this.trackCheckoutEvent('order_created', {
          order_id: response.data.data.order.id,
          checkout_id: this.checkoutId,
          amount: orderPayload.netTotal,
          currency: this.currency
        })

        return {
          success: true,
          order_id: response.data.data.order.id,
          order: response.data.data.order
        }
      } catch (error) {
        console.error('❌ [CREATE_ORDER] Failed to create order:', error);
        console.error('❌ [CREATE_ORDER] Error response:', error.response?.data);
        console.error('❌ [CREATE_ORDER] Error stack:', error.stack);

        // Track order creation failure
        this.trackCheckoutEvent('order_creation_failed', {
          checkout_id: this.checkoutId,
          error: error.message
        })

        throw new Error(error.response?.data?.message || error.message || 'Failed to create order in database')
      }
    },

    async updateOrderWithPayment(paymentData) {
      try {
        console.log('🔄 [UPDATE_ORDER] Updating order with payment details...');
        console.log('🔄 [UPDATE_ORDER] Timestamp:', new Date().toISOString());
        console.log('🔄 [UPDATE_ORDER] Payment data:', paymentData);

        const updatePayload = {
          order_id: paymentData.order_id,
          paypal_order_id: paymentData.paypal_order_id,
          authorization_key: paymentData.paypal_transaction_id,
          payment_status: 'completed',
          order_status: 'Processing',
          completion_date: new Date(),
          // Update shipping address with actual data from PayPal
          ship_address: paymentData.shipping_address.address,
          ship_city: paymentData.shipping_address.city,
          ship_state: paymentData.shipping_address.state,
          ship_zipcode: paymentData.shipping_address.postal_code,
          ship_country: paymentData.shipping_address.country,
          email: paymentData.shipping_address.email,
          first_name: paymentData.shipping_address.first_name,
          last_name: paymentData.shipping_address.last_name,
          mobile: paymentData.shipping_address.phone,
          notes_append: `PayPal payment captured at ${new Date().toISOString()}. Transaction ID: ${paymentData.paypal_transaction_id}`
        }

        console.log('📤 [UPDATE_ORDER] Sending update payload to API:', updatePayload);

        const response = await axios.post(this.baseURL + 'process-payment-paypal', updatePayload)

        console.log('✅ [UPDATE_ORDER] Order updated successfully!');
        console.log('✅ [UPDATE_ORDER] Response:', response.data);

        if (response.data.error) {
          throw new Error(response.data.error)
        }

        // Track payment completion in analytics
        this.trackCheckoutEvent('payment_captured', {
          order_id: paymentData.order_id,
          transaction_id: paymentData.paypal_transaction_id,
          amount: this.amount + this.dynamicShippingAmount + this.dynamicTaxAmount + this.dynamicShippingTaxAmount
        })

        return {
          success: true,
          order: response.data.data
        }
      } catch (error) {
        console.error('❌ [UPDATE_ORDER] Failed to update order:', error);
        console.error('❌ [UPDATE_ORDER] Error response:', error.response?.data);
        console.error('❌ [UPDATE_ORDER] Error stack:', error.stack);

        // Track payment update failure
        this.trackCheckoutEvent('payment_update_failed', {
          order_id: paymentData.order_id,
          error: error.message
        })

        throw new Error(error.response?.data?.message || error.message || 'Failed to update order with payment')
      }
    },

    trackCheckoutEvent(eventName, eventData = {}) {
      try {
        console.log(`📊 [ANALYTICS] Event: ${eventName}`, eventData);

        // Send to backend analytics endpoint
        axios.post(this.baseURL + 'analytics/track-event', {
          event_name: eventName,
          event_data: {
            ...eventData,
            timestamp: new Date().toISOString(),
            session_id: this.checkoutId,
            currency: this.currency,
            user_agent: navigator.userAgent
          }
        }).catch(err => {
          // Don't fail the checkout if analytics fails
          console.warn('⚠️ [ANALYTICS] Failed to track event:', err)
        })
      } catch (error) {
        console.warn('⚠️ [ANALYTICS] Error tracking event:', error)
      }
    },

    async processPayment(paymentMethod, paymentToken, additionalData = {}) {
      try {
        console.log('💳 Processing payment with method:', paymentMethod)
        console.log('💳 Payment token:', paymentToken)
        console.log('💳 Additional data:', additionalData)
        console.log('💳 Current checkout ID:', this.checkoutId)
        
        if (!this.checkoutId) {
          throw new Error('No checkout ID available for payment processing')
        }
        
        const paymentPayload = {
          checkout_id: this.checkoutId,
          payment_method: paymentMethod,
          payment_token: paymentToken,
          customer_data: this.getCustomerData(),
          shipping_address: this.getShippingData(),
          billing_address: this.getShippingData(),
          additional_data: additionalData
        }
        
        console.log('📦 Payment payload:', paymentPayload)
        console.log('📡 Making payment API call to:', this.baseURL + 'express-checkout/process-payment')
        
        const response = await axios.post(this.baseURL + 'express-checkout/process-payment', paymentPayload)
        
        console.log('💳 Payment response status:', response.status)
        console.log('💳 Payment response data:', response.data)

        if (response.data && response.data.success) {
          console.log('✅ Payment successful, handling success...')
          this.handlePaymentSuccess(response.data)
        } else {
          const errorMsg = response.data?.message || 'Payment processing failed - no success flag'
          console.error('❌ Payment failed:', response.data)
          throw new Error(errorMsg)
        }
      } catch (error) {
        console.error('❌ Payment processing error:', error)
        console.error('❌ Payment error response status:', error.response?.status)
        console.error('❌ Payment error response data:', error.response?.data)
        console.error('❌ Payment error stack:', error.stack)
        
        // Provide more specific error messages
        if (error.response?.status === 404) {
          throw new Error('Checkout session not found. Please try starting the checkout again.')
        } else if (error.response?.status === 422) {
          throw new Error('Payment validation failed: ' + (error.response?.data?.message || 'Invalid payment data'))
        } else if (error.response?.status === 500) {
          throw new Error('Server error during payment processing. Please try again.')
        } else {
          throw new Error(error.response?.data?.message || error.message || 'Payment processing failed')
        }
      }
    },

    updateButtonStates() {
      const buttons = [this.paypalButton, this.gpayButton, this.applePayButton]
      
      buttons.forEach(button => {
        if (button) {
          if (this.isLoading || this.isProcessing) {
            button.disabled = true
            button.style.opacity = '0.6'
            button.style.cursor = 'not-allowed'
          } else {
            button.disabled = false
            button.style.opacity = ''
            button.style.cursor = 'pointer'
          }
        }
      })

      if (this.currentMethod && this.isLoading) {
        const activeButton = this.currentMethod === 'paypal' ? this.paypalButton :
                           this.currentMethod.includes('google_pay') ? this.gpayButton :
                           this.currentMethod.includes('apple_pay') ? this.applePayButton : null

        if (activeButton) {
          this.addLoadingToButton(activeButton)
        }
      }
    },

    addLoadingToButton(button) {
      if (!button.querySelector('.loading-indicator')) {
        const spinner = document.createElement('div')
        spinner.className = 'loading-indicator'
        spinner.innerHTML = '<div class="button-spinner"></div>'
        button.appendChild(spinner)
      }
    },

    removeLoadingFromButtons() {
      const indicators = document.querySelectorAll('.loading-indicator')
      indicators.forEach(indicator => indicator.remove())
    },

    async initializePaymentMethods() {
      // Check Google Pay availability
      await this.checkGooglePayAvailability()
      
      // Check Apple Pay availability
      this.checkApplePayAvailability()
    },

    async checkGooglePayAvailability() {
      try {
        // TEMPORARILY DISABLE GOOGLE PAY - Merchant configuration issue
        console.log('Google Pay is temporarily disabled - merchant setup required')
        this.isGooglePayAvailable = false
        return

        // First try native Google Pay detection
        if (window.google && window.google.payments) {
          console.log('Checking native Google Pay availability...')
          const paymentsClient = new window.google.payments.api.PaymentsClient({
            environment: process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'TEST'
          })

          const isReadyToPay = await paymentsClient.isReadyToPay({
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [{
              type: 'CARD',
              parameters: {
                allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                allowedCardNetworks: ['MASTERCARD', 'VISA', 'AMEX']
              }
            }]
          })

          this.isGooglePayAvailable = isReadyToPay.result
          if (this.isGooglePayAvailable) {
            this.googlePayClient = paymentsClient
            console.log('Native Google Pay is available and ready')
            return
          }
        }

        // Fallback to PayPal Google Pay component
        if (window.paypal && window.paypal.Googlepay) {
          console.log('Checking PayPal Google Pay component...')
          const googlePayComponent = window.paypal.Googlepay()

          const isEligible = await googlePayComponent.isEligible()
          this.isGooglePayAvailable = isEligible

          if (isEligible) {
            this.googlePayClient = googlePayComponent
            console.log('Google Pay via PayPal is available')
          } else {
            console.log('Google Pay via PayPal is not eligible')
          }
        } else {
          console.log('Neither native Google Pay nor PayPal Google Pay component available')
          this.isGooglePayAvailable = false
        }
      } catch (error) {
        console.warn('Google Pay not available:', error)
        this.isGooglePayAvailable = false
      }
    },

    checkApplePayAvailability() {
      try {
        if (window.paypal && window.paypal.Applepay) {
          const applePayComponent = window.paypal.Applepay()
          
          const config = {
            countryCode: 'US',
            currencyCode: this.currency,
            total: {
              label: this.merchantName,
              amount: this.amount.toFixed(2)
            }
          }
          
          const isEligible = applePayComponent.isEligible()
          this.isApplePayAvailable = isEligible
          
          if (isEligible) {
            console.log('Apple Pay via PayPal is available')
          } else {
            console.log('Apple Pay via PayPal is not eligible')
          }
        } else {
          console.log('PayPal Apple Pay component not available')
          this.isApplePayAvailable = false
        }
      } catch (error) {
        console.warn('Apple Pay via PayPal not available:', error)
        this.isApplePayAvailable = false
      }
    },

    getCustomerData() {
      // Use collected address from confirmation modal if available
      if (this.collectedShippingAddress) {
        console.log('🧑 Using collected address for customer data:', this.collectedShippingAddress)
        return {
          email: this.collectedShippingAddress.email || '',
          first_name: this.collectedShippingAddress.first_name || this.collectedShippingAddress.name?.split(' ')[0] || '',
          last_name: this.collectedShippingAddress.last_name || this.collectedShippingAddress.name?.split(' ').slice(1).join(' ') || '',
          phone: this.collectedShippingAddress.phone || ''
        }
      }
      
      // Fallback to original shipping address
      if (this.shippingAddress) {
        console.log('🧑 Using original shipping address for customer data:', this.shippingAddress)
        const nameParts = (this.shippingAddress.full_name || '').split(' ')
        return {
          email: this.shippingAddress.email || '',
          first_name: nameParts[0] || '',
          last_name: nameParts.slice(1).join(' ') || '',
          phone: this.shippingAddress.phone || ''
        }
      }
      
      console.log('⚠️ No address data available for customer data')
      return {
        email: '',
        first_name: 'Express',
        last_name: 'Customer',
        phone: ''
      }
    },

    getShippingData() {
      // Use collected address from confirmation modal if available
      if (this.collectedShippingAddress) {
        console.log('📦 Using collected address for shipping data:', this.collectedShippingAddress)
        return {
          first_name: this.collectedShippingAddress.first_name || this.collectedShippingAddress.name?.split(' ')[0] || '',
          last_name: this.collectedShippingAddress.last_name || this.collectedShippingAddress.name?.split(' ').slice(1).join(' ') || '',
          email: this.collectedShippingAddress.email || '',
          address: this.collectedShippingAddress.address || this.collectedShippingAddress.address_line_1 || '',
          address2: this.collectedShippingAddress.address2 || this.collectedShippingAddress.address_line_2 || '',
          city: this.collectedShippingAddress.city || '',
          state: this.collectedShippingAddress.state || '',
          postal_code: this.collectedShippingAddress.postal_code || this.collectedShippingAddress.zip || '',
          country: this.collectedShippingAddress.country || this.collectedShippingAddress.country_code || 'US',
          phone: this.collectedShippingAddress.phone || ''
        }
      }
      
      // Fallback to original shipping address
      if (this.shippingAddress) {
        console.log('📦 Using original shipping address for shipping data:', this.shippingAddress)
        return {
          first_name: this.shippingAddress.full_name?.split(' ')[0] || '',
          last_name: this.shippingAddress.full_name?.split(' ').slice(1).join(' ') || '',
          email: this.shippingAddress.email || '',
          address: this.shippingAddress.address_line_1 || '',
          address2: this.shippingAddress.address_line_2 || '',
          city: this.shippingAddress.city || '',
          state: this.shippingAddress.state || '',
          postal_code: this.shippingAddress.postal_code || '',
          country: this.shippingAddress.country_code || 'US',
          phone: this.shippingAddress.phone || ''
        }
      }
      
      console.log('⚠️ No address data available for shipping data')
      return {
        first_name: 'Express',
        last_name: 'Customer',
        email: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
        phone: ''
      }
    },


    handlePaymentSuccess(data) {
      console.log('🎉 [PAYMENT_SUCCESS] Payment successful! Starting success handling...')
      console.log('🎉 [PAYMENT_SUCCESS] Success data received:', data)
      
      this.isProcessing = false
      this.isCompleted = true
      this.removeLoadingFromButtons()
      this.updateButtonStates()
      this.closePaymentPopup()
      
      this.orderId = data.order_id || data.order_number
      this.transactionId = data.transaction_id || this.checkoutId
      
      const successPayload = {
        checkoutId: this.checkoutId,
        orderId: this.orderId,
        transactionId: this.transactionId,
        amount: this.totalWithShippingAndTax,
        subtotal: this.amount,
        shippingAmount: this.dynamicShippingAmount,
        taxAmount: this.dynamicTaxAmount,
        shippingTaxAmount: this.dynamicShippingTaxAmount,
        paymentMethod: this.currentMethod,
        redirectUrl: data.redirect_url,
        needsShippingUpdate: data.needs_shipping_update || false
      }
      
      console.log('🎉 [PAYMENT_SUCCESS] Emitting payment-success event with payload:', successPayload)
      this.$emit('payment-success', successPayload)
      console.log('🎉 [PAYMENT_SUCCESS] Event emitted successfully')
    },

    handlePaymentError(errorMessage) {
      console.error('🚨 Handling payment error:', errorMessage)
      
      this.isProcessing = false
      this.isLoading = false
      this.error = errorMessage
      this.removeLoadingFromButtons()
      this.updateButtonStates()
      this.closePaymentPopup()
      
      // Don't close address confirmation modal if it's open - let user try again
      if (this.showAddressConfirmation) {
        console.log('⚠️ Keeping address confirmation modal open for retry')
      }
      
      console.log('📡 Emitting payment-error event:', {
        error: errorMessage,
        checkoutId: this.checkoutId,
        paymentMethod: this.currentMethod
      })
      
      this.$emit('payment-error', {
        error: errorMessage,
        checkoutId: this.checkoutId,
        paymentMethod: this.currentMethod
      })
    },

    resetCheckout() {
      this.isLoading = false
      this.isProcessing = false
      this.isCompleted = false
      this.error = null
      this.currentMethod = null
      this.checkoutId = null
      this.orderId = null
      this.transactionId = null
      this.showPayPalPopup = false
      this.showAddressConfirmation = false
      this.pendingPaymentData = null
      this.collectedShippingAddress = null
      this.editingAddress = false
      this.dynamicShippingAmount = 0
      this.dynamicTaxAmount = 0
      this.dynamicShippingTaxAmount = 0
      this.dynamicTotalAmount = 0
      this.removeLoadingFromButtons()
      this.updateButtonStates()
    },

    redirectToSuccess() {
      if (this.orderId) {
        this.router.push({ name: 'PaymentSuccess' });
      }
    },

    getPaymentMethodName(method) {
      const names = {
        paypal: 'PayPal',
        google_pay_via_paypal: 'Google Pay',
        apple_pay_via_paypal: 'Apple Pay'
      }
      return names[method] || method
    },

    formatAmount(amount, currency) {
      // Debug logging to see what values are being passed
      console.log('🔍 formatAmount called with:', { amount, currency });
      
      // Fallback to USD if currency is empty or undefined
      const currencyCode = currency || 'USD';
      console.log('🔍 Using currency code:', currencyCode);
      
      // Use appropriate locale based on currency to ensure consistent formatting
      const locale = currencyCode === 'CAD' ? 'en-CA' : 'en-US';
      
      try {
        const formatted = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode
        }).format(amount);
        console.log('🔍 Formatted result:', formatted);
        return formatted;
      } catch (error) {
        console.error('🚨 formatAmount error:', error);
        // Fallback formatting
        return `$${parseFloat(amount || 0).toFixed(2)}`;
      }
    },

    // Address confirmation methods
    showAddressConfirmationModal(shippingAddress) {
      console.log('🏠 [SHOW_ADDRESS_MODAL] Starting to show address confirmation modal')
      console.log('🏠 [SHOW_ADDRESS_MODAL] Received shipping address:', shippingAddress)
      console.log('🏠 [SHOW_ADDRESS_MODAL] Address fields breakdown:', {
        name: shippingAddress?.name,
        first_name: shippingAddress?.first_name,
        last_name: shippingAddress?.last_name,
        email: shippingAddress?.email,
        phone: shippingAddress?.phone,
        address: shippingAddress?.address,
        address_line_1: shippingAddress?.address_line_1,
        city: shippingAddress?.city,
        state: shippingAddress?.state,
        postal_code: shippingAddress?.postal_code,
        zip: shippingAddress?.zip,
        country: shippingAddress?.country,
        country_code: shippingAddress?.country_code
      })
      
      this.collectedShippingAddress = shippingAddress
      this.showAddressConfirmation = true
      this.isProcessing = false // Make sure processing state is cleared
      
      // Check if this is an empty address (needs editing)
      const isEmptyAddress = !this.hasCompleteAddress(shippingAddress)
      this.editingAddress = isEmptyAddress
      
      console.log('🏠 [SHOW_ADDRESS_MODAL] Address completeness check result:', {
        isEmptyAddress,
        editingAddress: this.editingAddress
      })
      
      if (isEmptyAddress) {
        console.log('📝 [SHOW_ADDRESS_MODAL] Empty address detected, starting in edit mode')
      } else {
        console.log('✅ [SHOW_ADDRESS_MODAL] Complete address detected, showing confirmation mode')
      }
      
      // Pre-populate editable address
      this.editableAddress = {
        first_name: shippingAddress.first_name || shippingAddress.name?.split(' ')[0] || '',
        last_name: shippingAddress.last_name || shippingAddress.name?.split(' ').slice(1).join(' ') || '',
        email: shippingAddress.email || '',
        phone: shippingAddress.phone || '',
        address: shippingAddress.address || shippingAddress.address_line_1 || '',
        address2: shippingAddress.address2 || shippingAddress.address_line_2 || '',
        city: shippingAddress.city || '',
        state: shippingAddress.state || '',
        postal_code: shippingAddress.postal_code || shippingAddress.zip || '',
        country: shippingAddress.country || shippingAddress.country_code || 'US'
      }
      
      console.log('✅ Address confirmation modal should now be visible')
    },

    closeAddressConfirmation() {
      this.showAddressConfirmation = false
      this.editingAddress = false
      this.collectedShippingAddress = null
    },

    handleOverlayClick() {
      // Only allow closing the modal by clicking outside if not in editing mode
      if (!this.editingAddress) {
        this.closeAddressConfirmation()
      }
      // If in editing mode, do nothing (modal stays open)
    },

    editAddress() {
      this.editingAddress = true
    },

    cancelEdit() {
      this.editingAddress = false
    },

    handleSaveEditedAddressClick(event) {
      console.log('🔴 [SAVE_BUTTON_CLICK] Save & Continue button clicked!')
      console.log('🔴 [SAVE_BUTTON_CLICK] Event object:', event)
      console.log('🔴 [SAVE_BUTTON_CLICK] Event type:', event?.type)
      console.log('🔴 [SAVE_BUTTON_CLICK] Target:', event?.target)
      
      // Immediately prevent any default behavior
      if (event) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        console.log('🔴 [SAVE_BUTTON_CLICK] Event prevented!')
      }
      
      // Add a small delay to ensure the console.log is visible
      setTimeout(() => {
        console.log('🔴 [SAVE_BUTTON_CLICK] About to call saveEditedAddress...')
        this.saveEditedAddress(event)
      }, 100)
    },

    async saveEditedAddress(event = null) {
      if (!this.isAddressValid) {
        this.error = 'Please fill in all required fields'
        return
      }

      try {
        this.isLoading = true
        console.log('🚀 [SAVE_EDITED_ADDRESS] Starting save process')
        console.log('📦 [SAVE_EDITED_ADDRESS] Current pending payment data:', this.pendingPaymentData)
        console.log('📝 [SAVE_EDITED_ADDRESS] Editable address data:', this.editableAddress)
        
        // Check if we have pending payment data
        if (!this.pendingPaymentData) {
          console.error('❌ [SAVE_EDITED_ADDRESS] No pending payment data found!')
          throw new Error('No pending payment data found')
        }
        
        // Update the collected address with edited data
        this.collectedShippingAddress = {
          ...this.collectedShippingAddress,
          name: `${this.editableAddress.first_name} ${this.editableAddress.last_name}`,
          first_name: this.editableAddress.first_name,
          last_name: this.editableAddress.last_name,
          email: this.editableAddress.email,
          phone: this.editableAddress.phone,
          address: this.editableAddress.address,
          address2: this.editableAddress.address2,
          city: this.editableAddress.city,
          state: this.editableAddress.state,
          postal_code: this.editableAddress.postal_code,
          country: this.editableAddress.country
        }
        
        console.log('📝 [SAVE_EDITED_ADDRESS] Updated collected shipping address:', this.collectedShippingAddress)

        // Save address to backend and recalculate shipping/tax
        console.log('🌐 [SAVE_EDITED_ADDRESS] Confirming address with backend...')
        console.log('📦 [SAVE_EDITED_ADDRESS] Sending edited address to backend:', this.editableAddress)
        console.log('📦 [SAVE_EDITED_ADDRESS] editableAddress fields:', {
          first_name: this.editableAddress.first_name,
          last_name: this.editableAddress.last_name,
          email: this.editableAddress.email,
          phone: this.editableAddress.phone,
          address: this.editableAddress.address,
          address2: this.editableAddress.address2,
          city: this.editableAddress.city,
          state: this.editableAddress.state,
          postal_code: this.editableAddress.postal_code,
          country: this.editableAddress.country
        })
        await this.confirmAddressWithBackend(this.editableAddress)
        console.log('✅ [SAVE_EDITED_ADDRESS] Backend confirmation completed')
        
        // Close address confirmation and continue with payment
        console.log('🔒 [SAVE_EDITED_ADDRESS] Closing address confirmation modal')
        this.closeAddressConfirmation()
        
        console.log('💳 [SAVE_EDITED_ADDRESS] Starting payment processing continuation...')
        await this.continuePaymentProcessing()
        console.log('✅ [SAVE_EDITED_ADDRESS] Payment processing completed successfully')
        
      } catch (error) {
        this.error = 'Failed to save address: ' + error.message
        console.error('❌ [SAVE_EDITED_ADDRESS] Address save error:', error)
        console.error('❌ [SAVE_EDITED_ADDRESS] Error stack:', error.stack)
        this.isLoading = false
      }
    },

    handleConfirmAndProceedClick(event) {
      console.log('🔴 [BUTTON_CLICK] Use This Address button clicked!')
      console.log('🔴 [BUTTON_CLICK] Event object:', event)
      console.log('🔴 [BUTTON_CLICK] Event type:', event?.type)
      console.log('🔴 [BUTTON_CLICK] Target:', event?.target)
      
      // Immediately prevent any default behavior
      if (event) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        console.log('🔴 [BUTTON_CLICK] Event prevented!')
      }
      
      // Add a small delay to ensure the console.log is visible
      setTimeout(() => {
        console.log('🔴 [BUTTON_CLICK] About to call confirmAndProceed...')
        this.confirmAndProceed(event)
      }, 100)
    },

    async confirmAndProceed(event = null) {
      try {
        this.isLoading = true
        console.log('🚀 [CONFIRM_AND_PROCEED] Starting confirmation process')
        console.log('📦 [CONFIRM_AND_PROCEED] Current pending payment data:', this.pendingPaymentData)
        console.log('📋 [CONFIRM_AND_PROCEED] Current collected shipping address:', this.collectedShippingAddress)
        console.log('🌐 [CONFIRM_AND_PROCEED] Component state:', {
          showAddressConfirmation: this.showAddressConfirmation,
          editingAddress: this.editingAddress,
          isLoading: this.isLoading,
          isProcessing: this.isProcessing,
          checkoutId: this.checkoutId
        })
        
        // Check if we have collected shipping address
        if (!this.collectedShippingAddress) {
          console.error('❌ [CONFIRM_AND_PROCEED] No collected shipping address found!')
          throw new Error('No shipping address available for confirmation')
        }
        
        // Check if we have pending payment data
        if (!this.pendingPaymentData) {
          console.error('❌ [CONFIRM_AND_PROCEED] No pending payment data found!')
          throw new Error('No pending payment data found')
        }
        
        // Confirm address with backend and recalculate shipping/tax
        console.log('🌐 [CONFIRM_AND_PROCEED] Confirming address with backend...')
        await this.confirmAddressWithBackend(this.editableAddress)
        console.log('✅ [CONFIRM_AND_PROCEED] Backend address confirmation completed')
        
        // Close address confirmation
        console.log('🔒 [CONFIRM_AND_PROCEED] Closing address confirmation modal')
        this.closeAddressConfirmation()
        
        // Continue with payment processing
        console.log('💳 [CONFIRM_AND_PROCEED] Starting payment processing continuation...')
        await this.continuePaymentProcessing()
        console.log('✅ [CONFIRM_AND_PROCEED] Payment processing completed successfully')
        
      } catch (error) {
        this.error = 'Failed to confirm address: ' + error.message
        console.error('❌ [CONFIRM_AND_PROCEED] Address confirmation error:', error)
        console.error('❌ [CONFIRM_AND_PROCEED] Error stack:', error.stack)
        console.error('❌ [CONFIRM_AND_PROCEED] Error details:', {
          name: error.name,
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        })
        
        // Prevent any redirect or navigation
        event?.preventDefault?.()
        event?.stopPropagation?.()
        
        // Don't close the modal on error, let user try again
        this.isLoading = false
        return false
      }
    },

    async confirmAddressWithBackend(address) {
      try {
        console.log('🌐 Confirming address with backend:', address)
        console.log('🌐 Current checkout ID:', this.checkoutId)
        
        if (!this.checkoutId) {
          throw new Error('No checkout ID available for address confirmation')
        }
        
        const addressPayload = {
          checkout_id: this.checkoutId,
          shipping_address: {
            first_name: address.first_name || address.name?.split(' ')[0] || '',
            last_name: address.last_name || address.name?.split(' ').slice(1).join(' ') || '',
            email: address.email || '',
            phone: address.phone || '',
            address: address.address || address.address_line_1 || '',
            address2: address.address2 || address.address_line_2 || '',
            city: address.city || '',
            state: address.state || '',
            postal_code: address.postal_code || address.zip || '',
            country: address.country || address.country_code || 'US'
          },
          billing_address: {
            first_name: address.first_name || address.name?.split(' ')[0] || '',
            last_name: address.last_name || address.name?.split(' ').slice(1).join(' ') || '',
            email: address.email || '',
            phone: address.phone || '',
            address: address.address || address.address_line_1 || '',
            address2: address.address2 || address.address_line_2 || '',
            city: address.city || '',
            state: address.state || '',
            postal_code: address.postal_code || address.zip || '',
            country: address.country || address.country_code || 'US'
          }
        }
        
        console.log('📦 Sending address confirmation payload:', addressPayload)
        console.log('📡 Making API call to:', this.baseURL + 'express-checkout/confirm-address')
        
        const response = await axios.post(this.baseURL + 'express-checkout/confirm-address', addressPayload)
        
        console.log('🌐 Backend response status:', response.status)
        console.log('🌐 Backend response data:', response.data)

        if (response.data && response.data.success) {
          // Update dynamic amounts if recalculated
          if (response.data.shipping_amount !== undefined) {
            this.dynamicShippingAmount = response.data.shipping_amount
            console.log('💰 Updated shipping amount:', this.dynamicShippingAmount)
          }
          if (response.data.tax_amount !== undefined) {
            this.dynamicTaxAmount = response.data.tax_amount
            console.log('💰 Updated tax amount:', this.dynamicTaxAmount)
          }
          if (response.data.shipping_tax_amount !== undefined) {
            this.dynamicShippingTaxAmount = response.data.shipping_tax_amount
            console.log('💰 Updated shipping tax amount:', this.dynamicShippingTaxAmount)
          }
          if (response.data.total_amount !== undefined) {
            this.dynamicTotalAmount = response.data.total_amount
            console.log('💰 Updated total amount:', this.dynamicTotalAmount)
          }
          
          console.log('✅ Address confirmed with backend successfully')
          return response.data
        } else {
          const errorMsg = response.data?.message || 'Failed to confirm address - no success flag'
          console.error('❌ Backend returned non-success response:', response.data)
          throw new Error(errorMsg)
        }
      } catch (error) {
        console.error('❌ Backend address confirmation failed:', error)
        console.error('❌ Error response status:', error.response?.status)
        console.error('❌ Error response data:', error.response?.data)
        console.error('❌ Error stack:', error.stack)
        
        // Provide more specific error messages
        if (error.response?.status === 404) {
          throw new Error('Checkout session not found. Please try starting the checkout again.')
        } else if (error.response?.status === 422) {
          throw new Error('Invalid address data: ' + (error.response?.data?.message || 'Please check all fields'))
        } else if (error.response?.status === 500) {
          throw new Error('Server error occurred. Please try again.')
        } else {
          throw new Error(error.response?.data?.message || error.message || 'Failed to confirm address')
        }
      }
    },

    async continuePaymentProcessing() {
      console.log('🚀 [CONTINUE_PAYMENT] Starting payment processing continuation')
      console.log('📊 [CONTINUE_PAYMENT] Component state:', {
        pendingPaymentData: this.pendingPaymentData,
        checkoutId: this.checkoutId,
        isLoading: this.isLoading,
        isProcessing: this.isProcessing,
        collectedShippingAddress: this.collectedShippingAddress
      })
      
      // This will be called after address is confirmed
      if (this.pendingPaymentData) {
        console.log('💳 [CONTINUE_PAYMENT] Found pending payment data for:', this.pendingPaymentData.method)
        console.log('💳 [CONTINUE_PAYMENT] Full pending payment data:', this.pendingPaymentData)
        
        try {
          console.log('🚀 [CONTINUE_PAYMENT] Calling processPayment with:', {
            method: this.pendingPaymentData.method,
            token: this.pendingPaymentData.token,
            additionalData: this.pendingPaymentData.additionalData
          })
          
          await this.processPayment(
            this.pendingPaymentData.method,
            this.pendingPaymentData.token,
            this.pendingPaymentData.additionalData
          )
          
          // Clear pending payment data
          this.pendingPaymentData = null
          console.log('✅ [CONTINUE_PAYMENT] Payment processing completed successfully')
        } catch (error) {
          console.error('❌ [CONTINUE_PAYMENT] Payment processing error after address confirmation:', error)
          console.error('❌ [CONTINUE_PAYMENT] Payment error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            stack: error.stack
          })
          this.handlePaymentError('Payment processing failed: ' + error.message)
        }
      } else {
        console.warn('⚠️ [CONTINUE_PAYMENT] No pending payment data found to continue processing')
        console.warn('⚠️ [CONTINUE_PAYMENT] Current component state:', {
          currentMethod: this.currentMethod,
          checkoutId: this.checkoutId,
          isLoading: this.isLoading,
          isProcessing: this.isProcessing,
          pendingPaymentData: this.pendingPaymentData
        })
        this.handlePaymentError('No payment data found to continue processing')
      }
    },

    // Address extraction methods for different payment gateways
    extractPayPalShippingAddress(details) {
      console.log('🔍 Extracting PayPal shipping address from:', details)
      
      const shippingAddress = details?.purchase_units?.[0]?.shipping?.address
      const payerInfo = details?.payer
      
      console.log('PayPal shipping address:', shippingAddress)
      console.log('PayPal payer info:', payerInfo)
      
      if (shippingAddress || payerInfo) {
        const extractedAddress = {
          name: payerInfo?.name?.given_name && payerInfo?.name?.surname 
            ? `${payerInfo.name.given_name} ${payerInfo.name.surname}`
            : payerInfo?.name?.given_name || 'PayPal Customer',
          first_name: payerInfo?.name?.given_name || 'PayPal',
          last_name: payerInfo?.name?.surname || 'Customer',
          email: payerInfo?.email_address || '',
          phone: payerInfo?.phone?.phone_number?.national_number || '',
          address: shippingAddress?.address_line_1 || '',
          address2: shippingAddress?.address_line_2 || '',
          city: shippingAddress?.admin_area_2 || '',
          state: shippingAddress?.admin_area_1 || '',
          postal_code: shippingAddress?.postal_code || '',
          country: shippingAddress?.country_code || 'US'
        }
        
        console.log('✅ Extracted PayPal address:', extractedAddress)
        return extractedAddress
      }
      
      console.log('❌ No shipping address found in PayPal result')
      return null
    },

    extractGooglePayShippingAddress(result) {
      console.log('🔍 Extracting Google Pay shipping address from:', result)
      
      // Google Pay might have shipping address in different locations
      let shippingAddress = result?.shippingAddress || result?.paymentMethodData?.info?.shippingAddress
      
      // Also check for billing address as fallback
      const billingAddress = result?.paymentMethodData?.info?.billingAddress
      
      if (!shippingAddress && billingAddress) {
        console.log('📍 Using billing address as shipping address fallback')
        shippingAddress = billingAddress
      }
      
      if (shippingAddress) {
        const extractedAddress = {
          name: shippingAddress.name || `${shippingAddress.givenName || ''} ${shippingAddress.familyName || ''}`.trim() || 'Google Pay Customer',
          first_name: shippingAddress.givenName || shippingAddress.name?.split(' ')[0] || 'Google Pay',
          last_name: shippingAddress.familyName || shippingAddress.name?.split(' ').slice(1).join(' ') || 'Customer',
          email: result?.email || shippingAddress.email || '',
          phone: shippingAddress.phoneNumber || shippingAddress.phone || '',
          address: shippingAddress.address1 || shippingAddress.addressLines?.[0] || '',
          address2: shippingAddress.address2 || shippingAddress.addressLines?.[1] || '',
          city: shippingAddress.locality || shippingAddress.city || '',
          state: shippingAddress.administrativeArea || shippingAddress.state || '',
          postal_code: shippingAddress.postalCode || shippingAddress.zipCode || '',
          country: shippingAddress.countryCode || shippingAddress.country || 'US'
        }
        
        console.log('✅ Extracted Google Pay address:', extractedAddress)
        return extractedAddress
      }
      
      console.log('❌ No shipping address found in Google Pay result')
      return null
    },

    hasCompleteAddress(address) {
      if (!address) return false
      
      // Check if we have at least the essential fields
      const hasName = address.first_name || address.name
      const hasAddress = address.address
      const hasCity = address.city
      const hasCountry = address.country
      
      const isComplete = hasName && hasAddress && hasCity && hasCountry
      console.log('🔍 Address completeness check:', {
        hasName,
        hasAddress,
        hasCity,
        hasCountry,
        isComplete
      })
      
      return isComplete
    },

    createEmptyAddress(paymentMethod) {
      console.log(`📝 Creating empty address template for ${paymentMethod}`)
      return {
        name: `${paymentMethod} Customer`,
        first_name: paymentMethod,
        last_name: 'Customer',
        email: '',
        phone: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US'
      }
    },

    extractApplePayShippingAddress(result) {
      // For PayPal's Apple Pay component, address extraction may vary
      // This is a placeholder - actual structure depends on PayPal's implementation
      const contact = result?.shippingContact
      
      if (contact) {
        return {
          name: contact.givenName && contact.familyName 
            ? `${contact.givenName} ${contact.familyName}`
            : contact.givenName || 'Apple Pay Customer',
          first_name: contact.givenName || 'Apple Pay',
          last_name: contact.familyName || 'Customer',
          email: contact.emailAddress || '',
          phone: contact.phoneNumber || '',
          address: contact.addressLines?.[0] || '',
          address2: contact.addressLines?.[1] || '',
          city: contact.locality || '',
          state: contact.administrativeArea || '',
          postal_code: contact.postalCode || '',
          country: contact.countryCode || 'US'
        }
      }
      return null
    },

  }
}
</script>

<style scoped>
.express-checkout-container {
  position: relative;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 999;
  backdrop-filter: blur(2px);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0070ba;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-overlay p {
  font-size: 16px;
  color: #333;
  margin: 0;
  font-weight: 500;
}

/* Button Loading Indicators */
.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  padding: 8px;
}

.button-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.processing-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #0070ba;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.processing-container, .success-container, .error-container {
  text-align: center;
  padding: 40px 20px;
  border-radius: 12px;
  margin: 20px 0;
}

.processing-container {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.success-container {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.error-container {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.success-icon, .error-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 30px;
  font-weight: bold;
}

.success-icon {
  background: #28a745;
  color: white;
}

.error-icon {
  background: #dc3545;
  color: white;
}

.processing-note {
  font-size: 14px;
  color: #6c757d;
  margin-top: 10px;
}

.retry-btn, .success-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 15px;
  font-weight: 600;
  font-size: 16px;
}

.success-btn {
  background: #28a745;
}

.retry-btn:hover {
  background: #c82333;
}

.success-btn:hover {
  background: #218838;
}

/* Payment Popup Styles */
.payment-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.payment-popup {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.popup-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.popup-header h3 {
  flex: 1;
  margin: 0;
  font-size: 20px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #e9ecef;
  color: #333;
}

.popup-content {
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.payment-summary {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.payment-summary h4 {
  margin: 0 0 16px 0;
  font-size: 18px;
  color: #333;
}

.amount-display {
  font-size: 32px;
  font-weight: bold;
  color: #28a745;
  margin-bottom: 16px;
}

.amount-breakdown {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
}

.merchant-info {
  font-size: 16px;
  color: #666;
  margin-bottom: 16px;
}

.items-list {
  border-top: 1px solid #e9ecef;
  padding-top: 16px;
}

.item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f4;
}

.item-row:last-child {
  border-bottom: none;
}

.item-name {
  font-size: 14px;
  color: #333;
}

.item-price {
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

#paypal-popup-container {
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Payment containers */
#paypal-button-container,
#google-pay-button-container,
#apple-pay-button-container {
  margin: 20px 0;
  padding: 20px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

/* Address Confirmation Styles */
.address-popup {
  max-width: 700px;
}

.address-confirmation {
  text-align: left;
}

.confirmation-text {
  font-size: 16px;
  color: #333;
  margin-bottom: 20px;
  font-weight: 500;
}

.address-display {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.address-field {
  margin-bottom: 12px;
  line-height: 1.5;
}

.address-field strong {
  color: #333;
  font-weight: 600;
}

.address-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.confirm-btn, .edit-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-btn {
  background: #28a745;
  color: white;
}

.confirm-btn:hover {
  background: #218838;
}

.edit-btn {
  background: #6c757d;
  color: white;
}

.edit-btn:hover {
  background: #5a6268;
}

/* Address Form Styles */
.address-form h4 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-field.full-width {
  grid-column: 1 / -1;
}

.form-field label {
  margin-bottom: 6px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.form-field input,
.form-field select {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.form-field input:focus,
.form-field select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-field input:invalid {
  border-color: #dc3545;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.save-btn, .cancel-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-btn {
  background: #007bff;
  color: white;
}

.save-btn:hover:not(:disabled) {
  background: #0056b3;
}

.save-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.cancel-btn {
  background: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background: #5a6268;
}

/* Responsive Design */
@media (max-width: 768px) {
  .payment-popup {
    width: 95%;
    max-height: 90vh;
  }
  
  .popup-header {
    padding: 16px 20px;
  }
  
  .popup-content {
    padding: 20px;
  }
  
  .amount-display {
    font-size: 28px;
  }
  
  .processing-container, .success-container, .error-container {
    padding: 30px 15px;
  }
  
  .loading-overlay p {
    font-size: 14px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-field.full-width {
    grid-column: 1;
  }

  .address-actions,
  .form-actions {
    flex-direction: column;
  }

  .confirm-btn, .edit-btn, .save-btn, .cancel-btn {
    width: 100%;
  }
}
</style>